import type { Timestamp } from "firebase/firestore";

export interface CatalogTool {
  name: string;
  title: string;
  description: string;
  readOnly: boolean;
  /** Auth methods this tool works under. null = all of them. */
  methods: string[] | null;
}

/**
 * A tool as it appears on one connected account.
 *
 * The common denominator of the two kinds: a tool declared by a provider, and a tool
 * discovered from a Generic MCP account's server. The dashboard renders both the same way,
 * so it only needs this much of them.
 */
export interface ConnectionTool {
  name: string;
  title: string;
  description: string;
  readOnly: boolean;
  /**
   * True when the storage budget cost this tool some fidelity -- its description cut
   * short, its argument schema reduced to "any object", or both. The tool still works;
   * Refresh restores it while the budget allows, and enabled tools are degraded last.
   */
  trimmed?: boolean;
}

/**
 * The tool name the model sees, for one tool on one connected account.
 *
 * Composed here rather than served by the catalog, because it depends on *which* account
 * is being talked about: the same GitHub tool is `github__list_issues` on one connection
 * and `github_acme__list_issues` on another.
 */
export function qualifiedToolName(slug: string, toolName: string): string {
  return `${slug}__${toolName}`;
}

export interface ManualField {
  key: string;
  label: string;
  placeholder?: string;
  help?: string;
  secret?: boolean;
  /** Render as a multi-line textarea (e.g. one custom header per line). */
  multiline?: boolean;
  /**
   * Render as checkboxes instead of a text field. The value posted is the ticked
   * `value`s joined with commas, in the order declared here -- a provider may treat the
   * first ticked one as its default, so click order must not leak into it.
   */
  options?: ManualOption[];
}

/** One tick-box of a `ManualField` that declares `options`. */
export interface ManualOption {
  value: string;
  label: string;
  help?: string;
  /** Ticked when the form first opens. */
  default?: boolean;
}

/** One way to connect a provider: an OAuth redirect, or fields the user fills in. */
export interface AuthMethod {
  id: string;
  kind: "oauth2" | "manual";
  label: string;
  description: string;
  /** OAuth needs a client configured on the deployment; manual never does. */
  configured: boolean;
  fields: ManualField[];
  scopes: string[];
  /** Tool names reachable via this method, so the UI can show the tradeoff up front. */
  toolNames: string[];
}

export interface CatalogProvider {
  id: string;
  name: string;
  description: string;
  category: "dev" | "comms" | "docs" | "calendar" | "crm" | "other";
  icon: string;
  docsUrl: string;
  /** True when at least one auth method is usable on this deployment. */
  configured: boolean;
  authMethods: AuthMethod[];
  /**
   * The tools every account of this provider gets. Empty when `dynamicTools` is set --
   * those live on the connection, because they came from the account rather than from us.
   */
  tools: CatalogTool[];
  /**
   * This provider's tools belong to the connected account, not to the provider (Generic
   * MCP: they are read from whatever server the user pointed it at). Nothing in the catalog
   * can say what they are, so the UI must not try to -- read `Connection.tools` instead.
   */
  dynamicTools: boolean;
  /**
   * Set when connections of this provider can be narrowed to specific resources (GitHub:
   * repositories) -- the card gets a picker. The list itself is fetched per connection
   * (`listConnectionResources`); only the words are in the catalog.
   */
  resources?: { noun: string; hint: string } | null;
}

/** One selectable resource in the picker. Mirrors ProviderResource in providers/types.ts. */
export interface ProviderResource {
  id: string;
  label: string;
  detail?: string;
}

/** Mirrors TeamDoc in functions/src/firebase.ts. Keep the two in step. */
export interface Team {
  id: string;
  name: string;
  /** The creator. `ownerUids` is what authorises owner actions -- see ownersOf(). */
  ownerUid: string;
  /** Absent on teams from before multiple owners existed. Read via ownersOf(). */
  ownerUids?: string[];
  memberUids: string[];
  /** uid -> email, filled as members create, join, or next load the dashboard. */
  memberEmails?: Record<string, string>;
  inviteCode: string;
  personal?: boolean;
  createdAt: Timestamp | null;
}

/** Mirrors ownersOf() in functions/src/teams.ts. Keep the two in step. */
export function ownersOf(team: Team): string[] {
  return team.ownerUids && team.ownerUids.length > 0
    ? team.ownerUids
    : [team.ownerUid];
}

/**
 * Mirrors InviteDoc in functions/src/firebase.ts. Keep the two in step.
 * Only ever pending: answering or revoking deletes the document.
 */
export interface Invite {
  id: string;
  teamId: string;
  teamName: string;
  email: string;
  invitedBy: string;
  invitedByEmail: string;
  createdAt: Timestamp | null;
  expiresAt: Timestamp | null;
}

/** Mirrors ProjectDoc in functions/src/firebase.ts. Keep the two in step. */
export interface Project {
  id: string;
  teamId: string;
  name: string;
  createdBy: string;
  createdAt: Timestamp | null;
}

/**
 * Who a connection serves: `team` = every member of the project's team, `personal` =
 * only its owner. Absent on pre-team connections, which were all personal.
 */
export type ConnectionScope = "team" | "personal";

export interface ServerDoc {
  ownerUid: string;
  /** The project this server serves. Absent only on pre-project servers, briefly. */
  projectId?: string;
  name: string;
  tokenPrefix: string;
  /** Connection slugs that are switched on -- see Connection.slug. */
  enabledServices: string[];
  /** Tool allow-lists, keyed by connection slug. */
  enabledTools: Record<string, string[]> | null;
  createdAt: Timestamp | null;
  tokenRotatedAt: Timestamp | null;
  lastUsedAt: Timestamp | null;
  callCount: number;
}

/** One connected account. A provider can have several. */
export interface Connection {
  id: string;
  ownerUid: string;
  /** The project (and, denormalised, team) this connection lives in. */
  projectId?: string;
  teamId?: string;
  /** Absent on pre-team connections -- read via scopeOf(), never directly. */
  scope?: ConnectionScope;
  /**
   * Team connections only: the one switch the whole team shares. Absent on team rows
   * from before team-wide enablement -- read via `enablementFor()`, never directly.
   */
  teamEnabled?: boolean;
  /** Team connections only: the team-wide allow-list (null = every tool). */
  teamEnabledTools?: string[] | null;
  providerId: string;
  /**
   * The tool-name prefix for this account: `github`, `github_acme`. Absent on
   * connections made before multi-account, which were one per provider -- read it
   * through `slugOf()`, never directly.
   */
  slug?: string;
  /** Which auth method made this connection. Decides which tools are exposed. */
  authMethod?: string;
  status: "active" | "error" | "revoked";
  accountLabel: string;
  scopes: string[];
  /**
   * Resource ids this connection may touch (GitHub: repositories as "owner/name").
   * Absent or null = everything the credential can reach. On a team connection the
   * selection is team-wide, like `teamEnabledTools`.
   */
  allowedResources?: string[] | null;
  /**
   * The tools read from this account, for a provider that discovers them (Generic MCP).
   * Absent for every normal provider -- theirs come from the catalog.
   */
  tools?: ConnectionTool[];
  toolsRefreshedAt?: Timestamp | null;
  /**
   * How many usable tools the account offered when `tools` was last read. Larger than
   * `tools.length` means the storage caps dropped some; the card says so.
   */
  toolsOffered?: number | null;
  /**
   * Custom Tools connections only: the script-backed tools themselves, keyed by tool
   * name. The `tools` cache above is derived from this map.
   */
  customTools?: Record<string, CustomTool>;
  lastError: string | null;
  updatedAt: Timestamp | null;
}

/** Mirrors CustomToolDoc in functions/src/firebase.ts. Keep the two in step. */
export interface CustomTool {
  script: string;
  title: string;
  description: string;
  inputSchema: Record<string, unknown>;
  readOnly: boolean;
  updatedAtMs: number;
}

/** Mirrors slugOf() in functions/src/connections.ts. Keep the two in step. */
export function slugOf(conn: Connection): string {
  return conn.slug || conn.providerId;
}

export function methodOf(conn: Connection): string {
  return conn.authMethod || "oauth";
}

/** Mirrors scopeOf() in functions/src/connections.ts. Keep the two in step. */
export function scopeOf(conn: Connection): ConnectionScope {
  return conn.scope ?? "personal";
}

/**
 * Is this connection switched on for this server, and which of its tools may show?
 * Mirrors `enablementFor()` in functions/src/connections.ts -- keep the two in step,
 * or the dashboard shows switches in a state the MCP endpoint disagrees with.
 *
 * A personal connection's switch and allow-list are the caller's own, on their server
 * document. A team connection's live on the connection itself and are shared by the
 * whole team -- toggling flips it for every member at once. Team rows from before
 * team-wide enablement have neither field and fall back to the per-server state they
 * were governed by, until the first toggle stamps them.
 */
export function enablementFor(
  conn: Connection,
  server: Pick<ServerDoc, "enabledServices" | "enabledTools"> | null
): { enabled: boolean; allowList: string[] | null } {
  const slug = slugOf(conn);
  const perServer = {
    enabled: (server?.enabledServices ?? []).includes(slug),
    allowList: server?.enabledTools?.[slug] ?? null,
  };
  if (scopeOf(conn) !== "team") return perServer;

  return {
    enabled: conn.teamEnabled ?? perServer.enabled,
    // `undefined` (field absent) is the legacy fallback; `null` is a real value meaning
    // "every tool". The distinction is why this is not a `??`.
    allowList:
      conn.teamEnabledTools !== undefined ? conn.teamEnabledTools : perServer.allowList,
  };
}

/**
 * The tools one connected account exposes. Mirrors `toolsForConnection()` in
 * functions/src/connections.ts -- keep the two in step, and keep the answer in one place
 * here too: no component should be deciding for itself which kind of provider it has.
 *
 * A normal provider's tools come from the catalog, filtered by the method the account was
 * connected with (a Slack webhook can post and nothing else). A Generic MCP account's tools
 * were read from its own server and cached on the connection.
 *
 * One deliberate difference from the server's copy: for Custom Tools, whose scripts live on
 * the connection document, the server derives the list from `customTools` and ignores the
 * cache (`DynamicToolSource.toolsOf`). Here the cache is what there is -- rebuilding it
 * would mean shipping the built-in tools' descriptions to the browser twice over -- so a
 * toolbox can show one tool fewer than its MCP server serves, until the next meta-tool call
 * or Refresh rewrites the cache. Both agree about every tool the user can toggle.
 */
export function toolsForConnection(
  provider: CatalogProvider,
  conn: Connection
): ConnectionTool[] {
  if (provider.dynamicTools) return conn.tools ?? [];

  const method = methodOf(conn);
  return provider.tools.filter((t) => !t.methods || t.methods.includes(method));
}

export interface CallLog {
  id: string;
  providerId: string;
  /** The account the call went to. Absent on logs from before multi-account. */
  slug?: string;
  tool: string;
  ok: boolean;
  durationMs: number;
  error: string | null;
  ts: Timestamp | null;
}

/**
 * One file a tool generated and stored, as /files lists it.
 *
 * Not a Firestore document, which is why it is not watched like everything else here:
 * the bucket is the only record of these (see functions/src/storage.ts), so the page
 * fetches rather than subscribes. Timestamps are ISO strings for the same reason -- they
 * crossed a callable, not the Firestore SDK.
 */
export interface StoredFile {
  connectionId: string;
  /** Object name within the connection's folder; what a delete is addressed by. */
  name: string;
  /** The link that plays it, or null for an object stored without a download token. */
  url: string | null;
  contentType: string;
  bytes: number;
  createdAt: string;
  /** When retention will delete it. */
  expiresAt: string;
  /** What the producing tool recorded: prompt, model, title. */
  meta: Record<string, string>;
  /** Null when the connection that made it has since been disconnected. */
  providerId: string | null;
  accountLabel: string | null;
  slug: string | null;
}
